import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type {
  SetToolAccessRequest,
  SetToolKindsRequest,
  ToolAccessView,
  StoredToolAccess,
  ToolConfig,
  ToolConfigUpdateRequest,
  ToolKindsPreview,
  ToolKindsPreviewRequest,
} from '../../../types/backend';

export type { ToolConfig };

/** Partial update: only the override fields to change. */
export type ToolConfigUpdate = Omit<ToolConfigUpdateRequest, 'action_id'>;

export const createToolConfigService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listToolConfigs: async (): Promise<ToolConfig[]> => rpcCall(client, 'toolset.ListToolConfigs'),

    upsertToolConfig: async (actionId: string, update: ToolConfigUpdate): Promise<ToolConfig> =>
      rpcCall(client, 'toolset.UpsertToolConfig', { action_id: actionId, ...update }),

    previewToolKinds: async (request: ToolKindsPreviewRequest): Promise<ToolKindsPreview> =>
      rpcCall(client, 'toolset.PreviewToolKinds', request),

    setToolKinds: async (request: SetToolKindsRequest): Promise<ToolConfig> =>
      rpcCall(client, 'toolset.SetToolKinds', request),

    getToolAccess: async (actionId: string): Promise<ToolAccessView> =>
      rpcCall(client, 'toolset.GetToolAccess', { action_id: actionId }),

    setToolAccess: async (request: SetToolAccessRequest): Promise<ToolAccessView> =>
      rpcCall(client, 'toolset.SetToolAccess', request),

    listToolAccess: async (): Promise<StoredToolAccess[]> => rpcCall(client, 'toolset.ListToolAccess', {}),
  };
};

export const useToolConfigService = () => {
  const { idToken } = useAuth();
  return useMemo(() => (idToken ? createToolConfigService(idToken) : null), [idToken]);
};
